// ============================================
// pages/storage/GenerateStorageBillForm.tsx — real API create/edit
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, XCircle, Warehouse } from 'lucide-react';
import { seasonsAPI, commoditiesAPI, godownsAPI, storageBillsAPI, storageBillPaymentsAPI } from '../../api/services';
import { ServicePaymentsSection } from '../../components/ServicePaymentsSection';
import { InvoiceUploadCard } from '../../components/InvoiceUploadCard';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const empty = {
  season_id: 0, commodity_id: 0, godown_id: 0,
  bill_no: '', bill_date: new Date().toISOString().slice(0, 10),
  bill_month: new Date().getMonth() + 1, bill_year: new Date().getFullYear(),
  num_bags: 0, num_days: 30, rate_per_bag_per_month: 6.94,
  discount_pct: 10, cgst_pct: 9, sgst_pct: 9,
  previous_balance: 0, payment_released: 0, tds_amount: 0,
  remarks: '',
};

export const GenerateStorageBillForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = !idParam || idParam === 'new' || idParam === 'create';
  const recordId = isCreate ? null : parseInt(idParam);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [form, setForm] = useState<any>(empty);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isLocked = ['submitted', 'dm_verified', 'manager_approved'].includes(status);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, g] = await Promise.all([seasonsAPI.list(), commoditiesAPI.list(), godownsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setCommodities(c.data);
        setGodowns(g.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (isCreate) {
          setForm((f: any) => ({ ...f, season_id: active?.id || 0 }));
        } else if (recordId && active) {
          const res = await storageBillsAPI.get(active.id, recordId);
          const raw = res.data as any;
          setForm({ ...empty, ...raw });
          setStatus(raw.status || 'draft');
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-compute totals client-side
  const computed = useMemo(() => {
    const bags = num(form.num_bags);
    const days = num(form.num_days);
    const rate = num(form.rate_per_bag_per_month);
    const gross = +((bags * days * rate) / 30).toFixed(2);
    const discount = +((gross * num(form.discount_pct)) / 100).toFixed(2);
    const afterDiscount = gross - discount;
    const cgst = +((afterDiscount * num(form.cgst_pct)) / 100).toFixed(2);
    const sgst = +((afterDiscount * num(form.sgst_pct)) / 100).toFixed(2);
    const totalGst = cgst + sgst;
    const net = +(afterDiscount + totalGst).toFixed(2);
    const totalPayable = +(net + num(form.previous_balance)).toFixed(2);
    const balance = Math.max(+(totalPayable - num(form.payment_released) - num(form.tds_amount)).toFixed(2), 0);
    return { gross, discount, cgst, sgst, totalGst, net, totalPayable, balance };
  }, [form.num_bags, form.num_days, form.rate_per_bag_per_month, form.discount_pct, form.cgst_pct, form.sgst_pct, form.previous_balance, form.payment_released, form.tds_amount]);

  const handleChange = (field: string, value: any) => setForm((f: any) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.season_id || !form.commodity_id || !form.godown_id) {
      setMessage({ type: 'error', text: 'Season, commodity and godown are required' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const seasonId = form.season_id;
      if (isCreate) {
        const res = await storageBillsAPI.create(seasonId, form);
        const newId = (res.data as any)?.id;
        setMessage({ type: 'success', text: 'Saved' });
        if (newId) navigate(`/storage/bills/edit/${newId}`, { replace: true });
      } else if (recordId) {
        await storageBillsAPI.update(seasonId, recordId, form);
        setMessage({ type: 'success', text: 'Updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate('/storage')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">
        <ArrowLeft className="w-4 h-4" />Back to List
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Warehouse className="w-7 h-7 text-indigo-600" />
          {isCreate ? 'New Storage Bill' : `Storage Bill #${form.bill_no || recordId}`}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Status: <span className="font-semibold uppercase">{status}</span></p>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Season</label>
            <select value={form.season_id} onChange={(e) => handleChange('season_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
            <select value={form.commodity_id} onChange={(e) => handleChange('commodity_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Godown</label>
            <select value={form.godown_id} onChange={(e) => handleChange('godown_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {godowns.map((g) => (<option key={g.id} value={g.id}>{g.godown_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bill No</label>
            <input type="text" value={form.bill_no || ''} onChange={(e) => handleChange('bill_no', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bill Date</label>
            <input type="date" value={form.bill_date || ''} onChange={(e) => handleChange('bill_date', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bill Period</label>
            <div className="flex gap-2">
              <select value={form.bill_month} onChange={(e) => handleChange('bill_month', parseInt(e.target.value))}
                disabled={isLocked}
                className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
                {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
              </select>
              <input type="number" value={form.bill_year} onChange={(e) => handleChange('bill_year', parseInt(e.target.value) || new Date().getFullYear())}
                disabled={isLocked} className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
            </div>
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Charges</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Number of Bags</label>
            <input type="number" value={form.num_bags || ''} onChange={(e) => handleChange('num_bags', parseInt(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Days Stored</label>
            <input type="number" value={form.num_days || ''} onChange={(e) => handleChange('num_days', parseInt(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Rate / Bag / Month</label>
            <input type="number" step="0.0001" value={form.rate_per_bag_per_month || ''} onChange={(e) => handleChange('rate_per_bag_per_month', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Discount %</label>
            <input type="number" step="0.01" value={form.discount_pct || ''} onChange={(e) => handleChange('discount_pct', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">CGST %</label>
            <input type="number" step="0.01" value={form.cgst_pct || ''} onChange={(e) => handleChange('cgst_pct', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">SGST %</label>
            <input type="number" step="0.01" value={form.sgst_pct || ''} onChange={(e) => handleChange('sgst_pct', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Previous Balance</label>
            <input type="number" step="0.01" value={form.previous_balance || ''} onChange={(e) => handleChange('previous_balance', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment Released</label>
            <input type="number" step="0.01" value={form.payment_released || ''} onChange={(e) => handleChange('payment_released', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Auto-Calculated</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div><p className="text-xs text-gray-500">Gross</p><p className="text-base font-bold">{formatAmount(computed.gross)}</p></div>
            <div><p className="text-xs text-gray-500">Discount</p><p className="text-base font-bold text-orange-600">-{formatAmount(computed.discount)}</p></div>
            <div><p className="text-xs text-gray-500">CGST + SGST</p><p className="text-base font-bold">{formatAmount(computed.totalGst)}</p></div>
            <div><p className="text-xs text-gray-500">Net Amount</p><p className="text-base font-bold text-indigo-700">{formatAmount(computed.net)}</p></div>
            <div><p className="text-xs text-gray-500">Previous Bal</p><p className="text-base font-bold">{formatAmount(num(form.previous_balance))}</p></div>
            <div className="col-span-2 md:col-span-1"><p className="text-xs text-gray-500">Total Payable</p><p className="text-base font-bold text-indigo-900">{formatAmount(computed.totalPayable)}</p></div>
            <div><p className="text-xs text-gray-500">Balance to Release</p><p className="text-base font-bold text-red-600">{formatAmount(computed.balance)}</p></div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={form.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)}
            disabled={isLocked} rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={handleSave} disabled={saving || isLocked}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? 'Save Draft' : 'Update'}
          </button>
        </div>
      </div>

      {/* Payment Installments — only after the bill is saved */}
      {!isCreate && recordId && (
        <ServicePaymentsSection
          parentId={recordId}
          api={storageBillPaymentsAPI}
          totalOwed={computed.totalPayable}
          canEdit={!isLocked}
          label="godown / warehouse"
          onChange={(released, balance) => {
            setForm((f: any) =>
              f.payment_released === released && f.balance_to_release === balance
                ? f
                : { ...f, payment_released: released, balance_to_release: balance },
            );
          }}
        />
      )}

      {!isCreate && recordId && form.season_id && (
        <InvoiceUploadCard
          seasonId={form.season_id}
          recordId={recordId}
          api={storageBillsAPI}
          record={form}
          locked={isLocked}
          label="Godown / Warehouse Invoice"
          description="Upload the storage contractor's invoice (PDF, PNG, or JPG — max 10 MB)."
          onChange={(u) => setForm((f: any) => ({ ...f, ...u }))}
        />
      )}
    </div>
  );
};

export default GenerateStorageBillForm;
